/**
 * Cart line quantity control — extracted from StorePOS so its three modes are
 * unit-testable. Behaviour is unchanged from the inline version:
 *
 *   1. weight_tracked (item.sold_weight_lbs set): READ-ONLY exact weight +
 *      remove. No stepper / no editable field — the tag weight cannot be edited
 *      out of sync with sold_weight_lbs and the pound pool. Full precision.
 *   2. ordinary unit line: - / editable count / + stepper.
 *   3. weight-based BYOC line: editable lbs input.
 */
import React, { useEffect, useState } from 'react';

interface CartQuantityInputProps {
  value: string | number;
  onChange: (newQty: number) => void;
  onRemove: () => void;
  isWeightBased: boolean;
  style: React.CSSProperties;
}

export const CartQuantityInput: React.FC<CartQuantityInputProps> = ({
  value, onChange, onRemove, isWeightBased, style,
}) => {
  const [localValue, setLocalValue] = useState(value.toString());

  // Sync local state when external value changes (e.g., +/- buttons)
  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  const commitValue = () => {
    const qty = isWeightBased ? parseFloat(localValue) : parseInt(localValue);
    if (isNaN(qty) || qty <= 0 || localValue === '' || localValue === '0') {
      onRemove();
    } else {
      onChange(qty);
    }
  };

  return (
    <input
      type="number"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={commitValue}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
      onFocus={(e) => e.target.select()}
      style={style}
      min="0"
      step={isWeightBased ? '0.01' : '1'}
    />
  );
};

interface CartItemQuantityControlProps {
  item: {
    id: number;
    quantity: string;
    is_weight_based: boolean;
    sold_weight_lbs?: string | null;
  };
  onUpdateQuantity: (itemId: number, qty: number) => void;
  onRemove: (itemId: number) => void;
  styles: Record<string, React.CSSProperties>;
}

export const CartItemQuantityControl: React.FC<CartItemQuantityControlProps> = ({
  item, onUpdateQuantity, onRemove, styles,
}) => {
  // 1. weight_tracked: read-only exact weight (full precision), remove-to-change.
  if (item.sold_weight_lbs) {
    return (
      <div style={styles.cartItemActions}>
        <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
          {parseFloat(item.sold_weight_lbs).toFixed(2)} lb
        </span>
        <button onClick={() => onRemove(item.id)} style={styles.qtyBtn} title="Remove package">
          ×
        </button>
      </div>
    );
  }
  // 2. ordinary unit line: stepper.
  if (!item.is_weight_based) {
    return (
      <div style={styles.cartItemActions}>
        <button onClick={() => onUpdateQuantity(item.id, parseInt(item.quantity) - 1)} style={styles.qtyBtn}>
          -
        </button>
        <CartQuantityInput
          value={item.quantity}
          onChange={(qty) => onUpdateQuantity(item.id, qty)}
          onRemove={() => onRemove(item.id)}
          isWeightBased={false}
          style={styles.qtyInput}
        />
        <button onClick={() => onUpdateQuantity(item.id, parseInt(item.quantity) + 1)} style={styles.qtyBtn}>
          +
        </button>
      </div>
    );
  }
  // 3. weight-based BYOC line: editable lbs input.
  return (
    <CartQuantityInput
      value={item.quantity}
      onChange={(qty) => onUpdateQuantity(item.id, qty)}
      onRemove={() => onRemove(item.id)}
      isWeightBased={true}
      style={styles.qtyInputWide}
    />
  );
};
